import React, { useEffect, useRef, useState } from "react"
import AutoSizer from "react-virtualized-auto-sizer";
import _ from "lodash";
import { FixedSizeList, ListChildComponentProps, VariableSizeList } from "react-window"
import { useAgTechSafeStateFunc } from "@agtech/core/utilities/AgTechUtilities";
import { useDataActionExecutor } from "@agtech/core/data/actions/AgTechDataActions";
import { AgTechGridEntity, initializeGridEntities, updateEntitiesOnItemsChanged, updateEntitiesForNewDefaultEntity } from "../Grids/Data/AgTechGridEntities"
import AgTechGridHeader from "../Grids/AgTechGridHeader"
import AgTechGridRow from "../Grids/AgTechGridRow"
import { AgTechGridProps, gridItems } from "../Grids/Data/AgTechGridProps"
import { AgTechGridData, useGridItemData } from "../Grids/Data/AgTechGridHooks"
import { AgTechGridState } from "../Grids/Data/AgTechGridState"
import { AgTechGridGroupHeaderRow, AgTechGridItemGroup } from "../Grids/Features/AgTechGridGrouping"
import { IAgTechGridItem } from "../Grids/Data/AgTechGridData";
import { AgTechGridSearchState } from "../Grids/Features/AgTechGridSearching";
import { useAgTechWebAppContext } from "../App/Contexts/AgTechWebAppContext";
import AgTechNoRecordsPage from "../Pages/Error/AgTechNoRecordsPage";
import { AgTechLoader } from "../Pages/Loading/AgTechLoadingSurface";
import { AgTechGridView } from "../Grids/Features/AgTechGridViews";

const GridHeaderHeightStandard = 32;
const GridHeaderHeightSmall = 28;

const GridRowHeightStandard = 38;
const GridRowHeightSmall = 30;

const AgTechGrid = <TItem extends IAgTechGridItem, TContextData = {}>(
    props: AgTechGridProps<TItem, TContextData>
) => {
    const appContext = useAgTechWebAppContext();
    const actionExecutor = useDataActionExecutor();

    const gridSurfaceRef = useRef<HTMLDivElement>(null);

    const gridProps = props.propModifier ? props.propModifier(props) : props;

    const gridData = gridProps.data.itemQuery.queryResult.data ?? [];

    const [gridState, updateGridState] = useAgTechSafeStateFunc<AgTechGridState<TItem, TContextData>>(() => {
        return {
            gridProps: gridProps,
            gridSurfaceRef: gridSurfaceRef,
            entityState: initializeGridEntities(gridProps),
            searchState: {
                filter: '',
                selectedField: {
                    field: '',
                    value: () => ''
                }
            },
            selectedView: gridProps.viewConfig?.views && undefined
        };
    });

    const isGridLoading = props.data.itemQuery.queryResult.isLoading
        || !gridState.current.entityState.hasLoaded;

    const gridItemData = useGridItemData({
        gridProps: gridProps,
        gridState: gridState,
        updateGridState: updateGridState,
        appContext: appContext,
        actionExecutor: actionExecutor
    });

    const isGridEmpty = gridState.current.entityState.hasLoaded
        && Object.keys(gridItemData.itemIndex).length === 0
        && (gridState.current.searchState?.filter ?? '') === '';

    useEffect(() => {
        updateEntitiesOnItemsChanged(
            gridProps,
            gridState.current.entityState,
            entityState => {
                updateGridState({
                    ...gridState.current,
                    entityState: entityState
                });
            }
        )
    }, [gridData]);

    useEffect(() => {
        if (!isGridLoading) {
            gridProps.behavior?.onGridDataLoaded?.(gridData.items);
        }
    }, [isGridLoading]);

    const handleGridSearchUpdated = (updatedSearchState: AgTechGridSearchState<TItem>) => {
        updateGridState({
            ...gridState.current,
            searchState: updatedSearchState
        });
    }

    const handleGridViewSelected = (selectedView: AgTechGridView<TItem>) => {
        updateGridState({
            ...gridState.current,
            selectedView: selectedView
        });
    }

    const handleCreateEntity = gridProps.editConfig?.createButtonText ? () => {
        updateEntitiesForNewDefaultEntity({
            gridState: () => gridState.current,
            updateGridState: updateGridState,
            updateGridEntities: updatedEntityState => {
                updateGridState({
                    ...gridState.current,
                    entityState: {
                        ...updatedEntityState,
                        allEntities: { ...updatedEntityState.allEntities },
                        versionNumber: updatedEntityState.versionNumber + 1
                    }
                });
            },
            updateGridEntity: (updatedEntityId, updatedEntity) => {
                const entity = gridState.current.entityState.allEntities[updatedEntityId];
                gridState.current.entityState.allEntities[updatedEntityId] = {
                    ...entity,
                    ...updatedEntity,
                    versionNumber: entity.versionNumber + 1
                };
                updateGridState({
                    ...gridState.current,
                    entityState: {
                        ...gridState.current.entityState,
                        versionNumber: gridState.current.entityState.versionNumber + 1,
                        allEntities: { ...gridState.current.entityState.allEntities }
                    }
                });
            },
            appContext: appContext,
            actionExecutor: actionExecutor
        });
    } : undefined;

    return isGridLoading ? (
        <AgTechLoader text='' />
    ) :
    isGridEmpty && gridProps.noRecordsConfig ? (
        <AgTechNoRecordsPage {...gridProps.noRecordsConfig} />
    ) : (
        <div className={`row-based wide overflow-hidden flex ${gridProps.groupingConfig ? 'grouped' : ''}`} ref={gridSurfaceRef}>
            {gridProps.header ? (
                <AgTechGridHeader
                    {...gridProps}
                    gridState={gridState.current}
                    onCreate={handleCreateEntity}
                    views={{
                        views: gridProps.viewConfig,
                        onViewSelected: handleGridViewSelected
                    }}
                    search={{
                        searchConfig: gridProps.searchConfig,
                        onSearchStateUpdated: handleGridSearchUpdated
                    }}
                />
            ) : null}
            <div className='overflow-hidden row-based border-b flex'>
                { isGridEmpty && gridProps.noRecordsConfig ? (
                    <AgTechNoRecordsPage {...gridProps.noRecordsConfig} />
                ) : (
                    <>
                        <AgTechGridTableHeader {...gridProps} />
                        <AgTechGridTableBody gridProps={gridProps} gridItemData={gridItemData} />
                    </>
                )}
            </div>
        </div>
    )
}

const AgTechGridTableHeader = <TItem extends IAgTechGridItem, TContextData = {}>(props: AgTechGridProps<TItem, TContextData>) => {
    let gridHeaderOffset = props.groupingConfig?.offset ?? undefined;
    let gridHeaderHeight = props.sizingConfig?.size === 'small' ? GridHeaderHeightSmall : GridHeaderHeightStandard;

    return (
        <div
            className='border wide bg-white items-center flex'
            style={{
                paddingLeft: gridHeaderOffset,
                height: gridHeaderHeight,
                minHeight: gridHeaderHeight
            }}
        >
            {props.selectionConfig && <div className="centered tall snug w-[36px] min-w-[36px] border-r flex " />}
            {props.editConfig && (props.editConfig.showStatus ?? true) && <div className="centered tall snug w-[36px] min-w-[36px]" />}

            {props.columns.map(column => {
                const shouldShowColumnHeader = column.showHeader ?? true;
                let columnSizeStyle: any = column.size ? { flex: column.size } : { flex: 1 };
                
                if (column.width) {
                    columnSizeStyle = { width: column.width };
                }

                return (
                    <div className={`row-based-vertically-centered container`} style={columnSizeStyle} key={column.header}>
                        {shouldShowColumnHeader ? (
                            <h1 className={`stretched font-semibold text-sm px-3 container ${column.classes ?? ''} !bg-white`}>{column.header}</h1>
                        ) : null}
                    </div>
                )
            })}

            { props.editConfig?.deleteItem && <div className="centered tall snug w-[36px] min-w-[36px]" style={{ width: '36px' }}></div>}
            { props.actions && (
                <div className="m-0 centered flex" style={{ width: props.actions?.gridActions?.width ?? 36 }}>
                    { props.actions.gridActions ? props.actions.gridActions.rendering() : null }
                </div>
            )}
        </div>
    )
}

type AgTechGridTableBodyProps<TItem extends IAgTechGridItem, TContextData = {}> = {
    gridProps: AgTechGridProps<TItem, TContextData>,
    gridItemData: AgTechGridData<TItem, TContextData>
}

const AgTechGridTableBody = <TItem extends IAgTechGridItem, TContextData = {}>(
    props: AgTechGridTableBodyProps<TItem, TContextData>
)  => {
    const gridHeaderHeight = props.gridProps.sizingConfig?.size === 'small' ? GridHeaderHeightSmall : GridHeaderHeightStandard;
    const gridRowHeight = props.gridProps.sizingConfig?.size === 'small' ? GridRowHeightSmall : GridRowHeightStandard;
    const gridHeight = _.sum(Object.values(props.gridItemData.itemHeights));
    const gridDefaultHeight = gridHeight + gridHeaderHeight;

    return (
        <div className="wide scrollable flex" style={{ maxHeight: '100%' }}>
            {props.gridProps.groupingConfig ? (
                <VariableSizeList
                    height={gridDefaultHeight - gridHeaderHeight}
                    itemData={props.gridItemData}
                    itemCount={Object.keys(props.gridItemData.itemIndex).length}
                    itemSize={index => props.gridItemData.itemHeights[index]}
                    width={'100%'}
                    className="overflow-y-auto border-r flex"
                >
                    {AgTechInternalGridRow}
                </VariableSizeList>
            ) : (
                <FixedSizeList
                    height={gridDefaultHeight - gridHeaderHeight}
                    itemData={props.gridItemData}
                    itemCount={Object.keys(props.gridItemData.itemIndex).length}
                    itemSize={gridRowHeight}
                    width={'100%'}
                    className="overflow-y-auto border-r flex"
                >
                    {AgTechInternalGridRow}
                </FixedSizeList>
            )}
        </div>
    )
}

// TODO: Memoize here
const AgTechInternalGridRow = <TItem extends IAgTechGridItem>(
    props: ListChildComponentProps<AgTechGridData<any, any>>
) => {
    const gridRowEntityId = (props.data.itemIndex[props.index] as any).id;

    if (gridRowEntityId !== undefined) {
        const gridRowEntity = props.data.itemIndex[props.index] as AgTechGridEntity<TItem>;

        return (
            <AgTechGridRow
                entity={gridRowEntity}
                context={props.data.context}
                state={gridRowEntity.state}
                columns={props.data.columns}
                index={props.index}
                style={{
                    ...props.style,
                    paddingLeft: props.data.configuration.groupingConfig?.offset ?? undefined
                }}
                hasRowChanged={props.data.hasRowChanged}
                gridConfiguration={props.data.configuration}
                handlers={props.data.handlers}
                isFocused={props.data.state.entityState.focusedEntityId === gridRowEntity.id}
            />
        )
    }
    else {
        const groupHeaderItem = props.data.itemIndex[props.index] as AgTechGridItemGroup<TItem, any>;
        const itemsInGroup = groupHeaderItem.itemsInGroup;

        return (
            <AgTechGridGroupHeaderRow
                groupKey={groupHeaderItem.groupKey}
                groupHeaderContent={groupHeaderItem.groupHeader}
                groupHeaderText={groupHeaderItem.groupText}
                groupItems={itemsInGroup}
                style={props.style}
            />
        )
    }
}

// export default React.memo(AgTechGrid, (prev, curr) => {
//     return prev.items.length === curr.items.length &&
//            curr.items.filter((item, index) => {

//            }).length > 0;
// }) as
// typeof AgTechGrid;

export default AgTechGrid;